import { db } from "services/firebase";

function asDate(value) {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  if (value.seconds != null) return new Date(value.seconds * 1000);
  return null;
}

export async function getInstitutionReadiness(institutionId) {
  if (!institutionId) return null;
  const snap = await db.collection("institutionReadiness").doc(institutionId).get();
  if (!snap.exists) return null;
  return {
    id: snap.id,
    ...snap.data(),
    lastUpdatedDate: asDate(snap.data()?.lastUpdated),
  };
}

export async function listInstitutionReadinessRows({ institutionIds = null } = {}) {
  if (Array.isArray(institutionIds) && institutionIds.length === 0) return [];

  let query = db.collection("institutionReadiness");
  if (Array.isArray(institutionIds) && institutionIds.length > 0) {
    const rows = await Promise.all(
      institutionIds.map((id) => db.collection("institutionReadiness").doc(id).get()),
    );
    return rows
      .filter((snap) => snap.exists)
      .map((snap) => ({
        id: snap.id,
        ...snap.data(),
        lastUpdatedDate: asDate(snap.data()?.lastUpdated),
      }));
  }

  const snap = await query.limit(200).get();
  return snap.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
    lastUpdatedDate: asDate(doc.data()?.lastUpdated),
  }));
}

export async function listOrganizationsForRegionalScope(regionId) {
  if (!regionId) return [];
  const snap = await db
    .collection("organizations")
    .where("primaryRegionId", "==", regionId)
    .limit(200)
    .get();
  return snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
}

export const READINESS_STATUS_COLORS = {
  active: "success",
  configuring: "warning",
  inactive: "default",
};

/** Chip map for loopHealthMetrics.loopStatus — mirrors tciManagerService STATUS_CHIP. */
export const LOOP_HEALTH_STATUS_CHIP = {
  healthy: { color: "success", label: "Healthy" },
  slowing: { color: "warning", label: "Slowing" },
  stalled: { color: "error", label: "Needs attention" },
};

/**
 * Load daily loopHealthMetrics for an institution, keyed by tciId.
 * Read-only; does not recompute status client-side.
 */
export async function listLoopHealthByTciForInstitution(institutionId) {
  if (!institutionId) return {};
  const snap = await db
    .collection("loopHealthMetrics")
    .where("institutionId", "==", institutionId)
    .limit(100)
    .get();
  const byTci = {};
  snap.docs.forEach((doc) => {
    const data = doc.data() || {};
    const tciId = data.tciId;
    if (!tciId) return;
    byTci[tciId] = {
      id: doc.id,
      ...data,
      computedAtDate: asDate(data.computedAt),
    };
  });
  return byTci;
}

export function readinessStripLabel(score) {
  if (score >= 80) return "Strong";
  if (score >= 50) return "Building";
  return "At risk";
}

export async function getLatestQuarterlySummary(institutionId) {
  if (!institutionId) return null;
  const snap = await db
    .collection("quarterlyLoopSummaries")
    .where("institutionId", "==", institutionId)
    .limit(5)
    .get();

  const rows = snap.docs
    .map((doc) => ({
      id: doc.id,
      ...doc.data(),
      generatedAtDate: asDate(doc.data()?.generatedAt),
    }))
    .sort((a, b) => (b.generatedAtDate?.getTime() || 0) - (a.generatedAtDate?.getTime() || 0));

  return rows[0] || null;
}
